import { Component, computed, input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { inject } from '@angular/core';
import { PortCallOvertimeSnapshotModel } from '../../../../shared/models/port-call-overtime-snapshot.model';
import { PortCallOvertimeSegment } from '../../../../shared/models/port-call-overtime-segment.model';
import { PortOvertimeLevel } from '../../../../shared/enums/port-overtime-level.enum';

interface OvertimeLevelGroup {
  level: PortOvertimeLevel | string;
  totalHours: number;
  breakdown: { reasonsLabel: string; hours: number }[];
}

@Component({
  selector: 'app-voyage-overtime-summary',
  imports: [TranslateModule],
  templateUrl: './voyage-overtime-summary.html',
})
export class VoyageOvertimeSummary {
  overtimeSnapshot = input<PortCallOvertimeSnapshotModel | null>(null);

  private translateService = inject(TranslateService);

  portOvertimeLevel = PortOvertimeLevel;

  headline = computed(() => {
    const reasons = this.uniqueReasons();
    if (reasons.length === 0) return null;
    return reasons.map((reason) => this.translateService.instant(`voyage.overtimeReason.${reason}`)).join(' · ');
  });

  levelGroups = computed<OvertimeLevelGroup[]>(() => {
    const snapshot = this.overtimeSnapshot();
    if (!snapshot) return [];

    return [PortOvertimeLevel.OT1, PortOvertimeLevel.OT2]
      .map((level) => {
        const segments = snapshot.segments.filter((segment) => segment.level === level);
        const totalHours = level === PortOvertimeLevel.OT1 ? snapshot.ot1Hours : snapshot.ot2Hours;

        return {
          level,
          totalHours: totalHours ?? 0,
          breakdown: this.groupSegmentsByReasons(segments),
        };
      })
      .filter((group) => group.totalHours > 0);
  });

  private uniqueReasons(): string[] {
    const snapshot = this.overtimeSnapshot();
    if (!snapshot) return [];

    const reasons = new Set<string>();
    snapshot.segments
      .filter((segment) => segment.level !== PortOvertimeLevel.REGULAR)
      .forEach((segment) => segment.reasons.forEach((reason) => reasons.add(reason)));

    return [...reasons];
  }

  private groupSegmentsByReasons(segments: PortCallOvertimeSegment[]): { reasonsLabel: string; hours: number }[] {
    const grouped = new Map<string, number>();

    segments.forEach((segment) => {
      const label = segment.reasons.length
        ? segment.reasons.map((reason) => this.translateService.instant(`voyage.overtimeReason.${reason}`)).join(' + ')
        : this.translateService.instant('voyage.overtimeReason.unspecified');
      grouped.set(label, (grouped.get(label) ?? 0) + segment.hours);
    });

    return [...grouped.entries()].map(([reasonsLabel, hours]) => ({ reasonsLabel, hours }));
  }
}
